/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BaseComponent, IconService } from '@isophi/core-ui';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, ReplaySubject, shareReplay, startWith, takeUntil } from 'rxjs';

@Component({
  selector: 'app-text-input',
  templateUrl: './text-input.component.html',
  styleUrls: ['./text-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class TextInputComponent extends BaseComponent implements ControlValueAccessor, OnInit {
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() childForm: FormGroup;

  @Input() type = 'text';

  @Input() formControlName: string;

  @Input() label: string;

  @Input() required? = false;

  @Input() formControlError?: string;

  @Input() customValidation? = false;

  @Input() customErrorName?: string;

  @Input() formControlCustomError?: string;

  @Input() placeholder?: string = '';

  @Input() requiredColor? = false;

  @Input() parentVisible? = false;

  @Input() tooltipVisible? = false;

  @Input() tooltipText?: string;

  @Input() readonly? = false;

  @Input() helpText?: string = '';

  @Input() asPassword?: boolean = false;

  @Input() withAsyncLoading?: boolean = false;

  @Input() set max(value: number) {
    this.maxInputLength$.next(value);
  }

  @Output() valueChange? = new EventEmitter<Event>();

  private readonly inputEvent$ = new ReplaySubject<Event>();

  protected readonly visible$ = new BehaviorSubject(false);

  protected readonly showLoading$ = new BehaviorSubject(false);
  protected showLoading = false;

  protected readonly maxInputLength$ = new BehaviorSubject<number | null>(null);
  protected readonly remainingChars$ = combineLatest([
    this.maxInputLength$,
    this.inputEvent$.pipe(
      map((event) => (event.target as HTMLInputElement).value.length),
      startWith(0)
    ),
  ]).pipe(
    map(([max, length]) => {
      if (!max) {
        return 0;
      }
      return max - length;
    }),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  constructor(protected iconService: IconService) {
    super();
  }

  ngOnInit(): void {
    this.childForm
      .get(this.formControlName)
      .statusChanges.pipe(takeUntil(this.ngUnsubscribe$), distinctUntilChanged())
      .subscribe((status) => {
        if (status !== 'PENDING') {
          this.showLoading$.next(false);
          this.showLoading = false;
        }
        if (status === 'PENDING' && this.withAsyncLoading) {
          this.showLoading$.next(true);
          this.showLoading = true;
        }
        // when using async validators, show invalid message right after it completes
        this.cdr.markForCheck();
      });
  }
  onInputChange(value: Event) {
    this.inputEvent$.next(value);
    this.valueChange.emit(value);
  }

  getFormControl(): FormControl {
    return this.childForm.get(this.formControlName) as FormControl;
  }

  onChange: any = () => {};

  onTouched: any = () => {};

  writeValue(value: any): void {
    this.onChange(value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
}
